import { useState, useEffect } from "react";
import API, { formatWIB } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { CandidateClaimBadge, CandidateClaimInfo, candidateRowClass } from "../components/CandidateClaimBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Check, X, ArrowRight, ArrowLeft, CheckSquare, Square, CaretLeft, CaretRight, FunnelSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { useTableSort } from "../hooks/useTableSort";

function formatIDR(num) {
  if (!num && num !== 0) return "-";
  return "Rp " + new Intl.NumberFormat("id-ID").format(num);
}

export default function ReviewQueuePage() {
  const [view, setView] = useState("jobs");
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Filters for jobs list
  const [filterSearch, setFilterSearch] = useState("");

  // Records within a job
  const [activeJob, setActiveJob] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsTotal, setRecordsTotal] = useState(0);

  // Filters for records
  const [recFilterType, setRecFilterType] = useState("all");
  const [recFilterSearch, setRecFilterSearch] = useState("");

  // Sortable headers — persisted ke localStorage, support multi-sort (shift+click)
  const sortAccessor = (rec, key) => {
    switch (key) {
      case "username": return (rec.username || "").toLowerCase();
      case "transaction_type": return (rec.transaction_type || "").toLowerCase();
      case "amount": return Number(rec.amount) || 0;
      case "account_info": return (rec.account_name || rec.account_number || "").toLowerCase();
      case "fund_method": return (rec.fund_method || "").toLowerCase();
      case "confirmed_by": return (rec.confirmed_by || "").toLowerCase();
      case "score": return Number(rec.confidence_score) || 0;
      case "candidates": return Number(rec.candidates?.length) || 0;
      case "transaction_date": return rec.transaction_date || "";
      case "notes": return (rec.notes || "").toLowerCase();
      default: return "";
    }
  };
  const { sortBy, clearSort, SortHeader, applySorts } = useTableSort({
    storageKey: "review-queue-sort",
    accessor: sortAccessor,
    testIdPrefix: "review-sort-header",
  });

  // Detail view
  const [selected, setSelected] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Bulk
  const [bulkSelected, setBulkSelected] = useState(new Set());

  const perPage = 20;

  useEffect(() => { loadGrouped(); }, [page]);

  const loadGrouped = async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * perPage;
      const { data } = await API.get(`/records/review/grouped?limit=${perPage}&skip=${skip}`);
      setJobs(data.jobs);
      setTotalJobs(data.total_jobs);
      setTotalRecords(data.total_records);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openJob = async (jobData) => {
    setActiveJob(jobData);
    setView("records");
    setBulkSelected(new Set());
    setRecFilterType("all");
    setRecFilterSearch("");
    try {
      const { data } = await API.get(`/records/review/job/${jobData.job.id}?limit=200`);
      setRecords(data.records);
      setRecordsTotal(data.total);
    } catch (e) { console.error(e); }
  };

  const handleReview = async (action) => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await API.post(`/records/${selected.id}/review`, { job_record_id: selected.id, action, selected_candidate_id: selectedCandidateId, notes: reviewNotes });
      setSelected(null); setReviewNotes(""); setSelectedCandidateId(null); setView("records");
      if (activeJob) await openJob(activeJob);
      loadGrouped();
    } catch (e) { alert("Review gagal"); }
    finally { setSubmitting(false); }
  };

  const handleBulkAction = async (action) => {
    if (bulkSelected.size === 0) return;
    if (!window.confirm(`${action === "approve" ? "APPROVE" : action === "reject" ? "REJECT" : "ABAIKAN"} ${bulkSelected.size} records?`)) return;
    setSubmitting(true);
    try {
      await API.post("/records/bulk-review", { record_ids: [...bulkSelected], action, notes: `Bulk ${action}` });
      setBulkSelected(new Set());
      if (activeJob) await openJob(activeJob);
      loadGrouped();
    } catch (e) { alert("Bulk action gagal"); }
    finally { setSubmitting(false); }
  };

  const toggleBulk = (id) => {
    setBulkSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  // Filtered jobs
  const filteredJobs = filterSearch
    ? jobs.filter(j => {
        const q = filterSearch.toLowerCase();
        return (j.job.site_name || "").toLowerCase().includes(q) ||
               (j.job.date_from || "").includes(q) || (j.job.date_to || "").includes(q) ||
               (j.job.id || "").toLowerCase().includes(q);
      })
    : jobs;

  // Filtered records
  const filteredRecordsRaw = records.filter(r => {
    if (recFilterType !== "all" && r.transaction_type !== recFilterType) return false;
    if (recFilterSearch) {
      const q = recFilterSearch.toLowerCase();
      if (!(r.username || "").toLowerCase().includes(q) && !(r.account_name || "").toLowerCase().includes(q) &&
          !(r.transaction_id || "").toLowerCase().includes(q) && !(r.fund_method || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const filteredRecords = applySorts(filteredRecordsRaw);


  const totalPages = Math.max(1, Math.ceil(totalJobs / perPage));

  // ============ DETAIL VIEW ============
  if (view === "detail" && selected) {
    return (
      <div data-testid="review-detail" className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { setSelected(null); setView("records"); setSelectedCandidateId(null); }} className="rounded-sm gap-1">
            <ArrowLeft size={14} /> Kembali
          </Button>
          <span className="text-sm font-mono text-zinc-400">
            <strong className="text-zinc-900">{selected.username}</strong> | {formatIDR(selected.amount)} | {selected.transaction_type}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-zinc-200 rounded-sm">
          <div className="border-r border-zinc-200">
            <div className="px-4 py-3 bg-zinc-900 text-white"><h3 className="text-xs font-semibold uppercase tracking-widest">Data Panel</h3></div>
            <div className="p-4 space-y-2">
              <FR label="Username" value={selected.username} />
              <FR label="Account" value={selected.account_name} />
              <FR label="Nominal" value={formatIDR(selected.amount)} hl />
              <FR label="Tipe" value={selected.transaction_type} />
              <FR label="Fund Method" value={selected.fund_method} />
              <FR label="Bank Details" value={selected.bank_details} />
              <FR label="Status Panel" value={selected.status_panel} />
              <FR label="Confirmed By" value={selected.confirmed_by} />
              <FR label="Tanggal" value={selected.transaction_date} />
              <FR label="Transaction ID" value={selected.transaction_id} />
            </div>
          </div>
          <div>
            <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200"><h3 className="text-xs font-semibold uppercase tracking-widest text-zinc-600">Kandidat Google Sheet</h3></div>
            {selected.candidates?.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {selected.candidates.map((c) => {
                  const isPicked = selectedCandidateId === c.id;
                  const isClaimed = !!c.claimed_by;
                  // Klaim warning state diutamakan kalau user pilih kandidat yg sudah di-claim
                  const baseCls = isPicked
                    ? "bg-emerald-50 border-l-2 border-emerald-500"
                    : candidateRowClass(c, true);
                  return (
                  <div key={c.id} className={`p-4 cursor-pointer transition-colors ${baseCls}`}
                    onClick={() => setSelectedCandidateId(c.id)}
                    data-testid={`review-candidate-${c.row_reference}`}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <StatusBadge status={c.match_type} />
                      <span className="text-sm font-mono font-bold">{c.score}%</span>
                      <span className="text-[10px] text-zinc-400 font-mono">{c.sheet_tab} / Row {c.row_reference}</span>
                      <div className="ml-auto flex items-center gap-2">
                        <CandidateClaimBadge candidate={c} size="xs" />
                        {isPicked && <span className="text-[10px] font-bold text-emerald-600">✓ DIPILIH</span>}
                      </div>
                    </div>
                    {isClaimed && (
                      <div className="mb-2">
                        <CandidateClaimInfo candidate={c} />
                        {isPicked && (
                          <p className="text-[11px] text-amber-700 font-semibold mt-1">
                            ⚠️ Anda memilih row yang sudah ter-claim panel lain. Approve hanya kalau Anda yakin override sengaja.
                          </p>
                        )}
                      </div>
                    )}
                    <div className="space-y-1">
                      {c.candidate_payload && Object.entries(c.candidate_payload)
                        .filter(([k]) => !["entries","split_details"].includes(k))
                        .map(([k,v]) => (
                          <div key={k} className="flex justify-between text-xs border-b border-zinc-100 pb-0.5">
                            <span className="text-zinc-500 font-mono">{k}</span>
                            <span className="text-zinc-900 font-mono">{k === "sheet_row" ? `Row ${v}` : (typeof v === "number" ? formatIDR(v) : String(v))}</span>
                          </div>
                        ))}
                    </div>
                    {c.amount_diff > 0 && <p className="text-xs text-amber-600 font-mono mt-1">Selisih: {formatIDR(c.amount_diff)}</p>}
                    {c.notes && <p className="text-xs text-zinc-500 mt-1 italic">{c.notes}</p>}
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center text-xs text-zinc-400">Tidak ada kandidat</div>
            )}
          </div>
        </div>

        <div className="border border-zinc-200 rounded-sm p-4 space-y-3">
          <Textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder="Catatan review (opsional)" className="rounded-sm border-zinc-300 font-mono text-xs h-16" />
          <div className="flex gap-2">
            <Button data-testid="approve-btn" onClick={() => handleReview("approve")} disabled={submitting || !selectedCandidateId} className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm gap-2"><Check size={16} /> Approve</Button>
            <Button data-testid="reject-btn" onClick={() => handleReview("reject")} disabled={submitting} variant="outline" className="rounded-sm gap-2 border-red-300 text-red-600 hover:bg-red-50"><X size={16} /> Reject</Button>
            <Button data-testid="ignore-btn" onClick={() => handleReview("ignore")} disabled={submitting} variant="outline" className="rounded-sm gap-2">Abaikan</Button>
          </div>
        </div>
      </div>
    );
  }

  // ============ RECORDS VIEW ============
  if (view === "records" && activeJob) {
    return (
      <div data-testid="review-records" className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => { setView("jobs"); setActiveJob(null); setBulkSelected(new Set()); }} className="rounded-sm gap-1">
              <ArrowLeft size={14} /> Semua Jobs
            </Button>
            <div>
              <h2 className="text-lg font-semibold text-zinc-900">Review: {activeJob.job.site_name || "Job"}</h2>
              <p className="text-xs font-mono text-zinc-400">{activeJob.job.id?.slice(0,8)} | {activeJob.job.date_from} ~ {activeJob.job.date_to} | {recordsTotal} records</p>
            </div>
          </div>
        </div>

        {/* Filter + Bulk Actions Bar */}
        <div className="border border-zinc-200 rounded-sm p-3 space-y-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Type quick toggle */}
              <div data-testid="review-filter-type" className="flex items-center gap-1">
                {(() => {
                  const counts = {
                    all: records.length,
                    deposit: records.filter(r => r.transaction_type === "deposit").length,
                    withdraw: records.filter(r => r.transaction_type === "withdraw").length,
                  };
                  const tabs = [
                    { k: "all", label: "Semua", color: "zinc" },
                    { k: "deposit", label: "Deposit", color: "emerald" },
                    { k: "withdraw", label: "Withdraw", color: "amber" },
                  ];
                  return tabs.map(t => {
                    const active = recFilterType === t.k;
                    const cm = {
                      zinc: "border-zinc-400 bg-zinc-700 text-white",
                      emerald: "border-emerald-400 bg-emerald-600 text-white",
                      amber: "border-amber-400 bg-amber-600 text-white",
                    };
                    return (
                      <button key={t.k}
                        data-testid={`review-type-${t.k}`}
                        onClick={() => setRecFilterType(t.k)}
                        disabled={counts[t.k] === 0 && t.k !== "all"}
                        className={`text-[11px] px-2.5 py-1 rounded-sm border transition-colors ${active ? cm[t.color] : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"} ${counts[t.k] === 0 && t.k !== "all" ? "opacity-40 cursor-not-allowed" : ""}`}>
                        {t.label} <span className="font-mono font-semibold ml-1">({counts[t.k]})</span>
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => handleBulkAction("approve")} disabled={submitting || bulkSelected.size === 0}
                className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm gap-1 text-xs h-8">
                <Check size={12} /> Approve {bulkSelected.size > 0 ? `(${bulkSelected.size})` : ""}
              </Button>
              <Button onClick={() => handleBulkAction("reject")} disabled={submitting || bulkSelected.size === 0}
                variant="outline" className="rounded-sm gap-1 text-xs h-8 border-red-300 text-red-600 hover:bg-red-50">
                <X size={12} /> Reject {bulkSelected.size > 0 ? `(${bulkSelected.size})` : ""}
              </Button>
              <Button onClick={() => handleBulkAction("ignore")} disabled={submitting || bulkSelected.size === 0}
                variant="outline" className="rounded-sm gap-1 text-xs h-8">
                Abaikan {bulkSelected.size > 0 ? `(${bulkSelected.size})` : ""}
              </Button>
            </div>
          </div>
          {/* Search input + counter */}
          <div className="flex items-center gap-2">
            <Input data-testid="review-search-input" value={recFilterSearch} onChange={e => setRecFilterSearch(e.target.value)}
              placeholder="Cari username, account name, bank, transaction ID..." className="rounded-sm border-zinc-300 h-8 text-xs w-72" />
            <span className="text-[10px] text-zinc-500 font-mono">
              Menampilkan {filteredRecords.length} dari {records.length} record
              {bulkSelected.size > 0 && <span className="text-violet-600 font-semibold ml-2">· {bulkSelected.size} dipilih</span>}
            </span>
            {sortBy.length > 0 && (
              <button data-testid="review-sort-reset" onClick={clearSort}
                className="text-[10px] font-mono text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-sm px-2 py-0.5 ml-auto"
                title="Reset semua sort">
                ⤺ Reset Sort ({sortBy.length})
              </button>
            )}
          </div>
        </div>

        <div className="border border-zinc-200 rounded-sm">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="px-2 py-2 w-8">
                  <button onClick={() => setBulkSelected(prev => prev.size === filteredRecords.length ? new Set() : new Set(filteredRecords.map(r => r.id)))}>
                    {bulkSelected.size > 0 && bulkSelected.size === filteredRecords.length ? <CheckSquare size={14} weight="fill" className="text-zinc-900" /> : <Square size={14} className="text-zinc-400" />}
                  </button>
                </th>
                <SortHeader k="username" label="Username" />
                <SortHeader k="transaction_type" label="Tipe" />
                <SortHeader k="amount" label="Nominal" align="right" />
                <SortHeader k="account_info" label="Account Info" />
                <SortHeader k="fund_method" label="Fund Method" />
                <SortHeader k="confirmed_by" label="Confirmed By" />
                <SortHeader k="score" label="Score" align="right" />
                <SortHeader k="candidates" label="Kandidat" align="right" />
                <SortHeader k="transaction_date" label="Tanggal" />
                <SortHeader k="notes" label="Notes" />
                <th className="px-3 py-2 w-20" />
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-sm text-zinc-400">Tidak ada record</td></tr>
              ) : filteredRecords.map((rec) => (
                <tr key={rec.id} className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${bulkSelected.has(rec.id) ? "bg-blue-50" : ""}`}>
                  <td className="px-2 py-2">
                    <button onClick={() => toggleBulk(rec.id)}>
                      {bulkSelected.has(rec.id) ? <CheckSquare size={14} weight="fill" className="text-zinc-900" /> : <Square size={14} className="text-zinc-400" />}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-zinc-900">{rec.username}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm ${rec.transaction_type === "deposit" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                      {rec.transaction_type === "deposit" ? "DEPO" : "WD"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-right">{formatIDR(rec.amount)}</td>
                  <td className="px-3 py-2 text-[10px] text-zinc-600 max-w-[140px] truncate" title={rec.account_name || ""}>{rec.account_name || "-"}</td>
                  <td className="px-3 py-2 text-[10px] text-zinc-500 max-w-[100px] truncate">{rec.fund_method || "-"}</td>
                  <td className="px-3 py-2 text-[10px] text-zinc-500 max-w-[100px] truncate" title={rec.confirmed_by || ""}>{rec.confirmed_by || "-"}</td>
                  <td className="px-3 py-2 text-xs font-mono text-right">{rec.confidence_score}</td>
                  <td className="px-3 py-2 text-xs font-mono text-right">{rec.candidates?.length || 0}</td>
                  <td className="px-3 py-2 text-[10px] font-mono text-zinc-500 whitespace-nowrap">{rec.transaction_date}</td>
                  <td className="px-3 py-2 text-[10px] text-zinc-400 min-w-[200px] max-w-[300px]" title={rec.notes || ""}>{rec.notes || "-"}</td>
                  <td className="px-3 py-2">
                    <Button onClick={() => { setSelected(rec); setView("detail"); }} variant="outline" size="sm" className="rounded-sm gap-1 text-xs">
                      Review <ArrowRight size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ============ JOBS LIST VIEW ============
  return (
    <div data-testid="review-queue-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">Review Queue</h1>
          <p className="text-sm text-zinc-500 mt-1">{totalRecords} records di {totalJobs} job menunggu review</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="border border-zinc-200 rounded-sm px-3 py-2 flex items-center gap-3">
        <FunnelSimple size={16} className="text-zinc-400" />
        <Input data-testid="review-filter-search" value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
          placeholder="Cari site, tanggal, ID job..." className="rounded-sm border-zinc-300 h-8 text-xs flex-1 max-w-xs" />
        <span className="text-[10px] text-zinc-400 font-mono">{filteredJobs.length} / {jobs.length} jobs</span>
        {filterSearch && (
          <button onClick={() => setFilterSearch("")} className="text-xs text-red-500 hover:text-red-700">Reset</button>
        )}
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-zinc-400">Loading...</div>
      ) : filteredJobs.length === 0 ? (
        <div className="border border-zinc-200 rounded-sm p-12 text-center">
          <Check size={32} className="text-emerald-400 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">{filterSearch ? "Tidak ada job yang sesuai filter." : "Semua record sudah di-review."}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredJobs.map((item) => (
            <div key={item.job.id} data-testid={`review-job-${item.job.id?.slice(0,8)}`}
              className="border border-zinc-200 rounded-sm p-4 hover:border-zinc-300 transition-colors cursor-pointer" onClick={() => openJob(item)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-amber-100 flex items-center justify-center text-amber-700 text-sm font-bold font-mono">
                    {item.review_count}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-900">{item.job.site_name || "Job"} — {item.job.date_from} ~ {item.job.date_to}</h3>
                    <p className="text-[10px] font-mono text-zinc-400">ID: {item.job.id?.slice(0,8)} | Tipe: {item.job.record_type || "all"} | Total: {item.job.total_records} records</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-xs font-mono text-amber-600 font-semibold">{item.review_count} menunggu</p>
                    <p className="text-[10px] text-zinc-400">{formatIDR(item.total_amount)}</p>
                  </div>
                  <ArrowRight size={16} className="text-zinc-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalJobs > perPage && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-zinc-500 font-mono">{(page-1)*perPage + 1}-{Math.min(page*perPage, totalJobs)} dari {totalJobs} jobs</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1} className="rounded-sm h-7 w-7 p-0"><CaretLeft size={14} /></Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages} className="rounded-sm h-7 w-7 p-0"><CaretRight size={14} /></Button>
          </div>
        </div>
      )}
    </div>
  );
}

function FR({ label, value, hl }) {
  return (
    <div className={`flex justify-between text-xs border-b border-zinc-100 pb-1 ${hl ? "bg-emerald-50/50 px-1 -mx-1" : ""}`}>
      <span className="text-zinc-500 font-mono uppercase tracking-wider">{label}</span>
      <span className="text-zinc-900 font-mono font-semibold text-right max-w-[60%] break-all">{value || "-"}</span>
    </div>
  );
}
